/**
 * Fetches upcoming shows directly from Bandsintown's public Events API
 * (https://rest.bandsintown.com) and renders them as real HTML into the
 * Tour section. app_id is a public application identifier, not a secret
 * (Bandsintown documents it as such, and it is already exposed in any
 * standard Bandsintown widget's HTML) — safe to call from the browser.
 */
(function () {
  function formatDate(isoLocal) {
    var match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoLocal || '');
    if (!match) return isoLocal || '';
    // Painted-poster "MM.DD" style, read straight from the API's venue-local digits so the
    // day shown never shifts with the visitor's own timezone.
    return match[2] + '.' + match[3];
  }

  function fullDate(isoLocal, locale) {
    var match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoLocal || '');
    if (!match) return '';
    var utcDate = new Date(Date.UTC(+match[1], +match[2] - 1, +match[3]));
    try {
      return new Intl.DateTimeFormat(locale || 'en', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      }).format(utcDate);
    } catch (e) {
      return '';
    }
  }

  function locationLabel(venue) {
    if (!venue) return '';
    var city = venue.city || '';
    var tail = venue.region || venue.country || '';
    return [city, tail].filter(Boolean).join(', ');
  }

  function buildFallbackUrl(artistId) {
    if (!artistId) return 'https://www.bandsintown.com/';
    return 'https://www.bandsintown.com/a/' + encodeURIComponent(artistId.replace(/^id_/, ''));
  }

  function renderEvents(eventsEl, events) {
    var frag = document.createDocumentFragment();

    events.forEach(function (event) {
      var row = document.createElement('div');
      row.className = 'tour-event';

      // <time> keeps the compact "10.03" visual while giving screen readers and machines
      // the unambiguous full date.
      var dateEl = document.createElement('time');
      dateEl.className = 'tour-event__date';
      var spoken = fullDate(event.datetime, document.documentElement.lang);
      if (spoken) {
        dateEl.dateTime = event.datetime.slice(0, 10);
        var shortEl = document.createElement('span');
        shortEl.setAttribute('aria-hidden', 'true');
        shortEl.textContent = formatDate(event.datetime);
        var spokenEl = document.createElement('span');
        spokenEl.className = 'visually-hidden';
        spokenEl.textContent = spoken;
        dateEl.appendChild(shortEl);
        dateEl.appendChild(spokenEl);
      } else {
        dateEl.textContent = formatDate(event.datetime);
      }
      row.appendChild(dateEl);

      var details = document.createElement('div');
      details.className = 'tour-event__details';

      var locationEl = document.createElement('div');
      locationEl.className = 'tour-event__location';
      locationEl.textContent = locationLabel(event.venue);
      details.appendChild(locationEl);

      // Shown exactly as entered in Bandsintown for Artists (some listings use the event or
      // festival name in the venue-name field).
      var venueText = ((event.venue && event.venue.name) || '').trim();
      if (venueText) {
        var venueEl = document.createElement('div');
        venueEl.className = 'tour-event__venue';
        venueEl.textContent = venueText;
        details.appendChild(venueEl);
      }

      row.appendChild(details);

      var offer = (event.offers || []).filter(function (o) {
        return o && o.url;
      })[0];
      var link = document.createElement('a');
      link.rel = 'noopener';
      link.target = '_blank';
      if (offer) {
        link.href = offer.url;
        link.className = 'tour-event__ticket';
        link.textContent = 'Tickets';
      } else if (event.url) {
        link.href = event.url;
        link.className = 'tour-event__ticket tour-event__ticket--muted';
        link.textContent = 'More info';
      } else {
        link.remove();
      }
      if (link.href) row.appendChild(link);

      frag.appendChild(row);
    });

    eventsEl.innerHTML = '';
    eventsEl.appendChild(frag);
  }

  function initTour(container) {
    var artistId = container.dataset.artistId;
    var appId = container.dataset.appId;
    var eventsEl = container.querySelector('[data-tour-events]');
    var statusEl = container.querySelector('[data-tour-status]');
    var footerLink = container.querySelector('[data-tour-fallback-link]');

    if (footerLink && !footerLink.getAttribute('href')) {
      footerLink.setAttribute('href', buildFallbackUrl(artistId));
    }

    if (!artistId || !appId || !eventsEl) {
      // Not configured — the section's own Liquid handles the empty/sample-data state.
      return;
    }

    function setStatus(html) {
      if (statusEl) {
        statusEl.innerHTML = html;
        statusEl.hidden = false;
      }
      eventsEl.hidden = true;
    }

    setStatus('Loading upcoming shows…');

    var url =
      'https://rest.bandsintown.com/artists/' +
      encodeURIComponent(artistId) +
      '/events/?app_id=' +
      encodeURIComponent(appId) +
      '&date=upcoming';

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('Bandsintown request failed: ' + response.status);
        return response.json();
      })
      .then(function (events) {
        if (!Array.isArray(events) || events.length === 0) {
          setStatus('No upcoming shows right now — check back soon.');
          return;
        }

        if (footerLink && events[0].artist && events[0].artist.url) {
          footerLink.setAttribute('href', events[0].artist.url);
        }

        renderEvents(eventsEl, events);
        if (statusEl) statusEl.hidden = true;
        eventsEl.hidden = false;
      })
      .catch(function () {
        setStatus(
          'Couldn’t load upcoming shows right now. <a href="' +
            buildFallbackUrl(artistId) +
            '" rel="noopener">See dates on Bandsintown</a>.'
        );
      });
  }

  function init() {
    document.querySelectorAll('[data-tour-bandsintown]').forEach(initTour);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      var container = event.target.querySelector('[data-tour-bandsintown]');
      if (container) initTour(container);
    });
  }
})();
